'use client';

import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { theme, i18n } from '@/config';
import { CopyIcon, SearchIcon, FilterIcon, CloseIconRed, ChevronIcon } from '@/components/icons';
import { Tooltip } from '@/components/ui-components/Tooltip/Tooltip';
import { Input } from '@/components/forms/controls/Input/Input';
import { Checkbox as CheckboxComponent } from '@/components/forms/controls/Checkbox/Checkbox';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs/Breadcrumbs';
import { Button } from '@/components/ui-components/Button/Button';
import { Dropdown } from '@/components/forms/controls/Dropdown/Dropdown';
import { DateRangePicker } from '@/components/forms/controls/DateRangePicker/DateRangePicker';
import { FilterModal } from '@/components/modals/FilterModal/FilterModal';
import type { FilterConfig, FilterValues } from '@/components/modals/FilterModal/FilterModal';
import type { TopErrorItem, AffectedDomain, LatestError } from '@/types/logAnalyzer';
import type { DropdownOption } from '@/types/components';
import { TimeWindowButtons } from './TimeWindowButtons';
import { SortDropdown } from './SortDropdown';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Clean domain name by removing "sites-" prefix and "-site" suffix
 */
const cleanDomainName = (domainName: string): string => {
  if (!domainName) return domainName;
  let cleaned = domainName;
  // Remove "sites-" prefix (case-insensitive)
  if (cleaned.toLowerCase().startsWith('sites-')) {
    cleaned = cleaned.slice(6);
  }
  // Remove "-site" suffix (case-insensitive)
  if (cleaned.toLowerCase().endsWith('-site')) {
    cleaned = cleaned.slice(0, -5);
  }
  return cleaned;
};

// ============================================================================
// Styled Components
// ============================================================================

const TableWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border.primary};
  box-sizing: border-box;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background: ${theme.colors.white};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
`;

const TableHead = styled.thead`
  background: #F5F5F5;
`;

const TableBody = styled.tbody`
  background-color: ${theme.colors.white};
`;

const TableRow = styled.tr<{ 
  $striped?: boolean; 
  $hoverable?: boolean;
  $isExpanded?: boolean;
  $isChild?: boolean;
  $isNested?: boolean;
  $isLastChild?: boolean;
  $isLastNested?: boolean;
  $hasNestedExpanded?: boolean;
  $clickable?: boolean;
}>`
  background-color: ${({ $isChild, $isNested }) => {
    // if ($isNested) return theme.colors.gray[50];
    // if ($isChild) return theme.colors.gray[25];
    return 'transparent';
  }};

  ${({ $isChild, $isNested }) => 
    !$isChild && !$isNested &&
    `
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border.primary};
  }
  `}

  ${({ $isChild, $isLastChild, $hasNestedExpanded }) =>
    $isChild && $isLastChild && !$hasNestedExpanded &&
    css`
      border-bottom: 1px solid ${theme.colors.border.primary};
    `}

  ${({ $isNested, $isLastNested }) =>
    $isNested && $isLastNested &&
    css`
      border-bottom: 1px solid ${theme.colors.border.primary};
    `}

  ${({ $hoverable, $clickable }) =>
    ($hoverable || $clickable) &&
    css`
    &:hover {
      background-color: ${theme.colors.gray[50]};
    }
  `}

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
    `}
`;

const TableHeader = styled.th<{ $align?: 'left' | 'center' | 'right'; $width?: string }>`
  padding: 14px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  width: ${({ $width }) => $width || 'auto'};
  border-bottom: 1px solid ${theme.colors.border.primary};
  font-weight: ${theme.fontWeights.semibold};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  background: #F5F5F5;
  font-family: 'Open Sans', sans-serif;

  &:first-child {
    padding-left: 24px;
  }
  &:last-child {
    padding-right: 24px;
  }
`;

const TableCell = styled.td<{ 
  $align?: 'left' | 'center' | 'right';
  $width?: string;
  $bold?: boolean;
  $isChild?: boolean;
  $isNested?: boolean;
  $isLastChild?: boolean;
  $hasNestedExpanded?: boolean;
}>`
  padding: 20px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  vertical-align: middle;
  font-size: ${theme.fontSizes.sm};
  font-weight: ${({ $bold }) => $bold ? theme.fontWeights.bold : theme.fontWeights.normal};
  color: ${theme.colors.gray[900]};
  word-break: break-word;
  font-family: 'Open Sans', sans-serif;
  position: relative;

   &:last-of-type {
     padding-right: 24px;
  }

  &:first-child {
    padding-left: ${({ $isChild, $isNested }) => {
      if ($isNested) return '130px';
      if ($isChild) return '60px';
      return '24px';
    }};
  }


  ${({ $isChild, $isLastChild, $hasNestedExpanded }) =>
    ($isChild && !$isLastChild && !$hasNestedExpanded) &&
    css`
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: ${theme.colors.border.primary};
      }

      &:first-child::after {
        left: 130px; /* Start after cell padding (60px) + checkbox (20px) + gap (12px) + icon (16px) = 108px */
      }
    `}
`;

const ExpandButton = styled.button<{ $expanded: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray[600]};
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(0deg)' : 'rotate(-90deg)')};

  &:hover {
    color: ${theme.colors.primary.accent};
  }

  &:focus {
    outline: 2px solid ${theme.colors.primary.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CheckboxCell = styled(TableCell)`
  width: 40px;
  padding-right: 8px;
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const MessageText = styled.span`
  flex: 1;
  word-break: break-word;
`;

const MessageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const MessageCopyButton = styled.button<{ $copied: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ $copied }) => ($copied ? theme.colors.primary.accent : theme.colors.gray[500])};
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    color: ${theme.colors.primary.accent};
  }

  &:focus {
    outline: 2px solid ${theme.colors.primary.accent};
    outline-offset: 2px;
    border-radius: 4px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const MessageTooltipContent = styled.div<{ $visible: boolean; $top?: number; $left?: number }>`
  position: fixed;
  background: #1f2937;
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  max-width: 600px;
  min-width: 300px;
  word-wrap: break-word;
  white-space: pre-wrap;
  z-index: 10000;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  box-shadow: ${theme.shadows.lg};
  max-height: 70vh;
  overflow-y: hidden;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  ${({ $top, $left }) => 
    $top !== undefined && $left !== undefined
      ? `
        top: ${$top}px;
        left: ${$left}px;
        transform: translateY(-100%) translateX(-20px);
      `
      : ''
  }

  /* Arrow */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 20px;
    border: 6px solid transparent;
    border-top-color: #1f2937;
  }
`;

const MessageTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const SeverityBadge = styled.span<{ $severity: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  text-transform: uppercase;
  color: ${({ $severity }) => {
    const severity = $severity.toLowerCase();
    if (severity === 'error' || severity === 'fatal') return '#dc2626';
    if (severity === 'warn' || severity === 'warning') return '#d97706';
    if (severity === 'info') return '#2563eb';
    if (severity === 'debug') return '#7c3aed';
    return '#6b7280';
  }};
  background-color: ${({ $severity }) => {
    const severity = $severity.toLowerCase();
    if (severity === 'error' || severity === 'fatal') return '#fee2e2';
    if (severity === 'warn' || severity === 'warning') return '#fef3c7';
    if (severity === 'info') return '#dbeafe';
    if (severity === 'debug') return '#f3e8ff';
    return '#f3f4f6';
  }};
`;

const LatestErrorContainer = styled.div`
  margin: 16px 0;
  padding: 16px;
  background: ${theme.colors.white};
  border: 1px solid #d9d9dc;
  border-left: 4px solid #ef4444;
  border-radius: 4px;
  position: relative;
`;

const LatestErrorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const LatestErrorTitle = styled.h4`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.bold};
  letter-spacing: 1px;
  color: ${theme.colors.gray[900]};
  margin: 0;
`;

const CopyButton = styled.button<{ $copied?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ $copied, theme }) => $copied ? '#FB6F2A' : theme.colors.gray[600]};
  font-size: ${theme.fontSizes.xs};
  border-radius: 4px;
  transition: all 0.2s;
  font-weight: ${({ $copied }) => $copied ? 600 : 400};

  &:hover {
    background: ${theme.colors.gray[100]};
    color: ${({ $copied, theme }) => $copied ? '#FB6F2A' : theme.colors.gray[900]};
    border-color: ${({ $copied }) => $copied ? '#10b981' : 'transparent'};
  }

  &:focus {
    outline: 2px solid ${theme.colors.primary.accent};
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ErrorDetails = styled.pre`
  margin: 0;
  padding: 24px;
  background: ${theme.colors.gray[50]};
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.gray[900]};
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  color: ${theme.colors.text.secondary};
`;

const SearchControlsContainer = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${theme.colors.border.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 250px;
`;

const SearchIconWrapper = styled.span`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.gray[550]};
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;
  height: 24px;
  width: 24px;
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const StyledSearchInput = styled(Input)`
  input {
    padding: 6px 16px !important;
    padding-left: 40px !important;
    border: 1px solid ${theme.colors.border.primary} !important;
    border-radius: 4px !important;
    font-size: 14px !important;
    font-family: 'Open Sans', sans-serif !important;
    
    &::placeholder {
      color: ${theme.colors.gray[550]} !important;
    }
    
    &:focus {
      border-color: ${theme.colors.status.info} !important;
      box-shadow: 0 0 0 3px ${theme.colors.overlay.focusInfo} !important;
    }
    
    &:hover {
      border-color: ${theme.colors.gray[400]} !important;
    }
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterButton = styled(Button)<{ $active?: boolean }>`
  ${({ $active }) =>
    $active &&
    css`
      border-color: ${theme.colors.primary.accent};
      color: ${theme.colors.primary.accent};
    `}
`;

const DateRangeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const ClearDateButton = styled.button`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${theme.colors.gray[500]};
  transition: color 0.2s;
  
  &:hover {
    color: ${theme.colors.gray[700]};
  }
`;


// ============================================================================
// Types
// ============================================================================

export interface DrilldownTableFilters {
  severity?: 'error' | 'warn' | 'fatal' | '';
  limit?: number;
  window?: string;
  start_date?: string;
  end_date?: string;
}

export interface DrilldownTableProps {
  data: TopErrorItem[];
  title?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearch?: (value: string) => void;
  striped?: boolean;
  hoverable?: boolean;
  className?: string;
  actions?: React.ReactNode;
  onSelectionChange?: (selectedIndices: Set<number>) => void;
  // Filter props
  filterable?: boolean;
  onFilterApply?: (filters: DrilldownTableFilters) => void;
  filterValues?: DrilldownTableFilters;
  timeWindowOptions?: Array<{ value: string; label: string }>;
  // Sort props
  sortBy?: 'count' | 'latest_error';
  onSortChange?: (sortBy: 'count' | 'latest_error') => void;
  // Error state
  errorMessage?: string;
}

// ============================================================================
// Component
// ============================================================================

export const DrilldownTable: React.FC<DrilldownTableProps> = ({
  data,
  title,
  searchable = false,
  searchPlaceholder = 'Search...',
  searchValue: controlledSearchValue,
  onSearch,
  striped = true,
  hoverable = true,
  className,
  actions,
  onSelectionChange,
  filterable = false,
  onFilterApply,
  filterValues,
  timeWindowOptions,
  sortBy = 'count',
  onSortChange,
  errorMessage,
}) => {
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const [copiedErrorKeys, setCopiedErrorKeys] = useState<Set<string>>(new Set());
  const [copiedMessages, setCopiedMessages] = useState<Set<number>>(new Set());
  const [hoveredMessageIndex, setHoveredMessageIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const tooltipRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<DrilldownTableFilters>({
    severity: filterValues?.severity || '',
    limit: filterValues?.limit || 20,
    window: filterValues?.window || '24h',
    start_date: filterValues?.start_date || '',
    end_date: filterValues?.end_date || '',
  });

  // Update local filters when filterValues prop changes
  useEffect(() => {
    if (filterValues) {
      setLocalFilters({
        severity: filterValues.severity || '',
        limit: filterValues.limit || 20,
        window: filterValues.window || '',
        start_date: filterValues.start_date || '',
        end_date: filterValues.end_date || '',
      });
    }
  }, [filterValues]);

  const handleFilterChange = (key: keyof DrilldownTableFilters, value: string | number) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = (filterValues: FilterValues) => {
    // Convert FilterValues to DrilldownTableFilters
    let limitValue = filterValues.limit ? parseInt(filterValues.limit as string, 10) : 20;
    
    // Enforce max limit of 500
    if (limitValue > 500) {
      limitValue = 500;
    }
    // Enforce min limit of 1
    if (limitValue < 1) {
      limitValue = 1;
    }
    
    const newFilters: DrilldownTableFilters = {
      ...localFilters,
      severity: (filterValues.severity as 'error' | 'warn' | 'fatal' | '') || '',
      limit: limitValue,
    };
    setLocalFilters(newFilters);
    onFilterApply?.(newFilters);
    setIsFilterModalOpen(false);
  };

  // Only check for filters inside the modal (severity and limit), not time window or date range
  const hasActiveFilters = useMemo(() => {
    return !!(
      localFilters.severity ||
      localFilters.limit !== 20
    );
  }, [localFilters]);

  // FilterModal configuration
  const filterConfig: FilterConfig = {
    filters: [
      {
        id: 'severity',
        label: 'Severity (Optional)',
        placeholder: 'Select severity',
        type: 'dropdown',
        options: [
          { label: 'All', value: '' },
          { label: 'Error', value: 'error' },
          // { label: 'Warn', value: 'warn' },
          { label: 'Fatal', value: 'fatal' },
        ],
      },
      {
        id: 'limit',
        label: 'Limit',
        placeholder: 'Enter limit',
        type: 'number',
        min: 1,
        max: 500,
      },
    ],
    showDateRange: false,
  };

  // Convert localFilters to FilterValues for FilterModal
  const filterModalValues: FilterValues = {
    severity: localFilters.severity || '',
    limit: localFilters.limit?.toString() || '20',
  };


  const handleDateRangeChange = (start: string, end: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      start_date: start,
      end_date: end,
      // Clear window when using date range
      window: '',
    }));
  };


  const searchValue = controlledSearchValue !== undefined ? controlledSearchValue : internalSearchValue;
  const handleSearchChange = (value: string) => {
    if (controlledSearchValue === undefined) {
      setInternalSearchValue(value);
    }
    onSearch?.(value);
  };

  const filteredData = useMemo(() => {
    if (!searchValue || !searchValue?.trim()) return data;
    const searchLower = searchValue.toLowerCase();
    return data.filter((error) => 
      error.message.toLowerCase().includes(searchLower) ||
      error.severity_type.toLowerCase().includes(searchLower)
    );
  }, [data, searchValue]);

  // Check if filters are applied (different from defaults)
  const hasFiltersApplied = useMemo(() => {
    if (!filterValues) return false;
    const defaultFilters: DrilldownTableFilters = {
      window: '24h',
      limit: 20,
      severity: '',
      start_date: '',
      end_date: '',
    };
    
    return (
      filterValues.window !== defaultFilters.window ||
      filterValues.limit !== defaultFilters.limit ||
      (filterValues.severity && filterValues.severity !== defaultFilters.severity) ||
      (filterValues.start_date && filterValues.start_date !== defaultFilters.start_date) ||
      (filterValues.end_date && filterValues.end_date !== defaultFilters.end_date)
    );
  }, [filterValues]);

  // Check if search is active
  const hasSearchActive = searchValue && typeof searchValue === 'string' && searchValue?.trim()?.length > 0;

  // Show headers if search is active, filters are applied, or there's an error message
  const shouldShowHeaders = hasSearchActive || hasFiltersApplied || !!errorMessage;

  // Calculate select all state
  const allSelected = filteredData?.length > 0 && selectedRows.size === filteredData?.length;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < filteredData?.length;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(filteredData.map((_, i) => i)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const toggleError = (index: number) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
        // Also collapse all domains for this error
        filteredData[index]?.affected_domains.forEach((_, domainIndex) => {
          const domainKey = `${index}-${domainIndex}`;
          setExpandedDomains((prevDomains) => {
            const nextDomains = new Set(prevDomains);
            nextDomains.delete(domainKey);
            return nextDomains;
          });
        });
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleDomain = (errorIndex: number, domainIndex: number) => {
    const domainKey = `${errorIndex}-${domainIndex}`;
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domainKey)) {
        next.delete(domainKey);
      } else {
        next.add(domainKey);
      }
      return next;
    });
  };

  const toggleRowSelection = (index: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange?.(selectedRows);
  }, [selectedRows, onSelectionChange]);

  const copyToClipboard = async (text: string, errorKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedErrorKeys((prev) => {
        const next = new Set(prev);
        next.add(errorKey);
        return next;
      });
      setTimeout(() => {
        setCopiedErrorKeys((prev) => {
          const next = new Set(prev);
          next.delete(errorKey);
          return next;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyMessageToClipboard = async (message: string, errorIndex: number) => {
    try {
      await navigator.clipboard.writeText(message);
      setCopiedMessages((prev) => {
        const next = new Set(prev);
        next.add(errorIndex);
        return next;
      });
      setTimeout(() => {
        setCopiedMessages((prev) => {
          const next = new Set(prev);
          next.delete(errorIndex);
          return next;
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to copy message to clipboard:', error);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return dateString;
    }
  };

  const formatErrorDetails = (error: LatestError): string => {
    return JSON.stringify(error, null, 2);
  };

  if (filteredData?.length === 0 && !shouldShowHeaders) {
    return (
      <TableWrapper className={className}>
        {title && (
          <Breadcrumbs items={[{ label: title }]} />
        )}
        <EmptyState>
          {errorMessage 
            ? errorMessage
            : hasFiltersApplied 
              ? i18n.logAnalyzer.operations.topErrors.noErrorsTryFilters
              : i18n.logAnalyzer.operations.topErrors.noErrors
          }
        </EmptyState>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper className={className}>
      {title && (
        <Breadcrumbs items={[{ label: title }]} />
      )}
      
      {(searchable || filterable || actions || onSortChange) && (
        <SearchControlsContainer>
          <LeftControls>
          {searchable && (
              <SearchWrapper>
                <SearchIconWrapper>
                  <SearchIcon width="16" height="16" />
                </SearchIconWrapper>
                <StyledSearchInput
                  type="search"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
                  fullWidth
                />
              </SearchWrapper>
            )}
            {filterable && (
              <FilterButton
                variant="outline"
                size="medium"
                onClick={() => setIsFilterModalOpen(true)}
                $active={hasActiveFilters}
              >
                <FilterIcon width={20} height={20} />
                Filter
              </FilterButton>
            )}
          </LeftControls>
          <RightControls>
            {/* Time Window Quick Links */}
            {filterable && (
              <TimeWindowButtons
                filters={localFilters}
                onFilterApply={(newFilters) => {
                  setLocalFilters(newFilters);
                  onFilterApply?.(newFilters);
                }}
                timeWindowOptions={timeWindowOptions}
              />
            )}
            
            {/* Date Range Picker */}
            {filterable && (
              <DateRangeWrapper>
                <DateRangePicker
                  startDate={localFilters.start_date || ''}
                  endDate={localFilters.end_date || ''}
                  onChange={(start: string, end: string) => {
                    // Convert dates to UTC ISO format
                    const convertToUTC = (dateStr: string): string => {
                      if (!dateStr) return '';
                      const date = new Date(dateStr + 'T00:00:00Z');
                      if (isNaN(date.getTime())) return '';
                      return date.toISOString();
                    };
                    
                    const convertEndDateToUTC = (dateStr: string): string => {
                      if (!dateStr) return '';
                      const date = new Date(dateStr + 'T23:59:59Z');
                      if (isNaN(date.getTime())) return '';
                      return date.toISOString();
                    };
                    
                    const startUTC = convertToUTC(start);
                    const endUTC = convertEndDateToUTC(end);
                    const newFilters = { ...localFilters, start_date: startUTC, end_date: endUTC, window: '' };
                    setLocalFilters(newFilters);
                    onFilterApply?.(newFilters);
                  }}
                  size="sm"
                />
                {(localFilters.start_date || localFilters.end_date) && (
                  <ClearDateButton
                    onClick={() => {
                      const newFilters = { ...localFilters, start_date: '', end_date: '' };
                      setLocalFilters(newFilters);
                      onFilterApply?.(newFilters);
                    }}
                    type="button"
                    aria-label="Clear date range"
                    title="Clear date range"
                  >
                    <CloseIconRed width={12} height={12} />
                  </ClearDateButton>
                )}
              </DateRangeWrapper>
            )}
            
            {onSortChange && sortBy && (
              <SortDropdown
                value={sortBy}
                options={[
                  { value: 'count', label: 'Count' },
                  { value: 'latest_error', label: 'Latest Error' },
                ]}
                onChange={onSortChange}
                name="sortBy"
              />
            )}
            {actions && <ActionsWrapper>{actions}</ActionsWrapper>}
          </RightControls>
        </SearchControlsContainer>
      )}

      <TableContainer>
        <StyledTable>
          <TableHead>
            <tr>
              <TableHeader $align="left">
                <MessageCellContent>
                  <StyledCheckboxWrapper>
                    <CheckboxComponent
                      checked={allSelected}
                      indeterminate={isIndeterminate}
                      onChange={toggleSelectAll}
                      aria-label={`Select all ${filteredData?.length} errors`}
                    />
                  </StyledCheckboxWrapper>
                  <span>{i18n.logAnalyzer.operations.topErrors.columns.message}</span>
                </MessageCellContent>
              </TableHeader>
              <TableHeader $align="center" $width="120px">{i18n.logAnalyzer.operations.topErrors.columns.severity}</TableHeader>
              <TableHeader $align="center" $width="100px">{i18n.logAnalyzer.operations.topErrors.columns.count}</TableHeader>
              <TableHeader $align="left" $width="180px">{i18n.logAnalyzer.operations.topErrors.columns.firstOccurrence}</TableHeader>
              <TableHeader $align="left" $width="180px">{i18n.logAnalyzer.operations.topErrors.columns.latestOccurrence}</TableHeader>
            </tr>
          </TableHead>
          <TableBody>
            {filteredData?.length === 0||errorMessage ? (
              <TableRow>
                <TableCell colSpan={5} style={{ textAlign: 'center', padding: theme.spacing.xl }}>
                  {errorMessage 
                    ? errorMessage
                    : hasFiltersApplied 
                      ? i18n.logAnalyzer.operations.topErrors.noErrorsTryFilters
                      : i18n.logAnalyzer.operations.topErrors.noErrors
                  }
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((error, errorIndex) => {
              const isErrorExpanded = expandedErrors.has(errorIndex);
              const hasAffectedDomains = error.affected_domains && error.affected_domains?.length > 0;

              return (
                <React.Fragment key={errorIndex}>
                  {/* Main Error Row */}
                  <TableRow 
                    $striped={striped} 
                    $hoverable={hoverable}
                    $clickable={hasAffectedDomains}
                    onClick={(e) => {
                      // Don't expand if clicking on checkbox or expand button
                      const target = e.target as HTMLElement;
                      if (target.closest('input[type="checkbox"]') || target.closest('button')) {
                        return;
                      }
                      if (hasAffectedDomains) {
                        toggleError(errorIndex);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Support keyboard navigation (Enter or Space)
                      if (hasAffectedDomains && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        const target = e.target as HTMLElement;
                        if (!target.closest('input[type="checkbox"]') && !target.closest('button')) {
                          toggleError(errorIndex);
                        }
                      }
                    }}
                    tabIndex={hasAffectedDomains ? 0 : undefined}
                    role={hasAffectedDomains ? 'button' : undefined}
                    aria-expanded={hasAffectedDomains ? isErrorExpanded : undefined}
                  >
                    <TableCell width="50%">
                      <MessageCellContent>
                        <StyledCheckboxWrapper onClick={(e) => e.stopPropagation()}>
                          <CheckboxComponent
                        checked={selectedRows.has(errorIndex)}
                            onChange={(checked) => {
                              toggleRowSelection(errorIndex);
                            }}
                            aria-label={`Select error: ${error.message.substring(0, 50)}...`}
                      />
                        </StyledCheckboxWrapper>
                      {hasAffectedDomains && (
                        <ExpandButton
                          $expanded={isErrorExpanded}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleError(errorIndex);
                            }}
                          aria-label={isErrorExpanded ? 'Collapse error details' : 'Expand error details'}
                        >
                          <ChevronIcon />
                        </ExpandButton>
                      )}
                        <MessageContainer>
                          <MessageTooltipWrapper
                            ref={(el) => {
                              if (el) {
                                tooltipRefs.current.set(errorIndex, el);
                              } else {
                                tooltipRefs.current.delete(errorIndex);
                              }
                            }}
                            onMouseEnter={() => {
                              if (error.message?.length > 100 && !copiedMessages.has(errorIndex)) {
                                setHoveredMessageIndex(errorIndex);
                                const element = tooltipRefs.current.get(errorIndex);
                                if (element) {
                                  const rect = element.getBoundingClientRect();
                                  const tooltipHeight = 200; // Approximate height
                                  const spaceAbove = rect.top;
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  
                                  let top: number;
                                  let left: number;
                                  
                                  // Position above if there's more space above, otherwise below
                                  if (spaceAbove > spaceBelow && spaceAbove > tooltipHeight) {
                                    top = rect.top - 8;
                                    left = rect.left;
                                  } else {
                                    top = rect.bottom + 8;
                                    left = rect.left;
                                  }
                                  
                                  // Ensure tooltip doesn't go off screen
                                  const maxLeft = window.innerWidth - 600; // max-width of tooltip
                                  left = Math.max(20, Math.min(left, maxLeft));
                                  
                                  setTooltipPosition({ top, left });
                                }
                              }
                            }}
                            onMouseLeave={() => {
                              if (!copiedMessages.has(errorIndex)) {
                                setHoveredMessageIndex(null);
                                setTooltipPosition(null);
                              }
                            }}
                          >
                            <MessageText>
                      {error.message?.length > 100 
                        ? `${error.message.substring(0, 100)}...` 
                        : error.message}
                            </MessageText>
                            {error.message?.length > 100 && (
                              <>
                                <MessageCopyButton
                                  $copied={copiedMessages.has(errorIndex)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyMessageToClipboard(error.message, errorIndex);
                                    setHoveredMessageIndex(errorIndex);
                                    const element = tooltipRefs.current.get(errorIndex);
                                    if (element) {
                                      const rect = element.getBoundingClientRect();
                                      setTooltipPosition({ top: rect.top - 8, left: rect.left });
                                    }
                                  }}
                                  onMouseEnter={() => {
                                    setHoveredMessageIndex(errorIndex);
                                    const element = tooltipRefs.current.get(errorIndex);
                                    if (element) {
                                      const rect = element.getBoundingClientRect();
                                      setTooltipPosition({ top: rect.top - 8, left: rect.left });
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    if (!copiedMessages.has(errorIndex)) {
                                      setHoveredMessageIndex(null);
                                      setTooltipPosition(null);
                                    }
                                  }}
                                  aria-label="Copy full error message"
                                  title="Copy full error message"
                                >
                                  <CopyIcon size={14} />
                                </MessageCopyButton>
                                {(hoveredMessageIndex === errorIndex || copiedMessages.has(errorIndex)) && (
                                  <MessageTooltipContent 
                                    $visible={hoveredMessageIndex === errorIndex || copiedMessages.has(errorIndex)}
                                    $top={tooltipPosition?.top}
                                    $left={tooltipPosition?.left}
                                  >
                                    {copiedMessages.has(errorIndex) ? 'Copied' : error.message}
                                  </MessageTooltipContent>
                                )}
                              </>
                            )}
                          </MessageTooltipWrapper>
                        </MessageContainer>
                      </MessageCellContent>
                    </TableCell>
                    <TableCell $align="center">
                      <SeverityBadge $severity={error.severity_type}>
                        {error.severity_type}
                      </SeverityBadge>
                    </TableCell>
                    <TableCell $align="center">
                      {error.count.toLocaleString()}
                    </TableCell>
                    <TableCell $align="center">
                      {error.first_occurrence ? formatDate(error.first_occurrence) : 'None'}
                    </TableCell>
                    <TableCell $align="center">
                      {formatDate(error.latest_occurrence)}
                    </TableCell>
                  </TableRow>

                  {/* Affected Domains Rows */}
                  {isErrorExpanded && hasAffectedDomains && error.affected_domains.map((domain, domainIndex) => {
                    const domainKey = `${errorIndex}-${domainIndex}`;
                    const isDomainExpanded = expandedDomains.has(domainKey);
                    const isLastDomain = domainIndex === error.affected_domains?.length - 1;

                    return (
                      <React.Fragment key={domainKey}>
                        {/* Domain Row */}
                        <TableRow 
                          $isChild 
                          $isLastChild={isLastDomain} 
                          $hasNestedExpanded={isDomainExpanded && !!domain.latest_error} 
                          $striped={striped} 
                          $hoverable={hoverable}
                          $clickable={!!domain.latest_error}
                          onClick={(e) => {
                            // Don't expand if clicking on expand button
                            const target = e.target as HTMLElement;
                            if (target.closest('button')) {
                              return;
                            }
                            if (domain.latest_error) {
                              toggleDomain(errorIndex, domainIndex);
                            }
                          }}
                          onKeyDown={(e) => {
                            // Support keyboard navigation (Enter or Space)
                            if (domain.latest_error && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              const target = e.target as HTMLElement;
                              if (!target.closest('button')) {
                                toggleDomain(errorIndex, domainIndex);
                              }
                            }
                          }}
                          tabIndex={domain.latest_error ? 0 : undefined}
                          role={domain.latest_error ? 'button' : undefined}
                          aria-expanded={domain.latest_error ? isDomainExpanded : undefined}
                        >
                          <TableCell $isChild $isLastChild={isLastDomain} $hasNestedExpanded={isDomainExpanded && !!domain.latest_error}>
                            <MessageCellContent>
                              <StyledCheckboxWrapper style={{ width: '20px' }}></StyledCheckboxWrapper>
                            <ExpandButton
                              $expanded={isDomainExpanded}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDomain(errorIndex, domainIndex);
                                }}
                              aria-label={isDomainExpanded ? 'Collapse domain details' : 'Expand domain details'}
                            >
                              <ChevronIcon />
                            </ExpandButton>
                              <MessageText>{cleanDomainName(domain.domain_name)}</MessageText>
                            </MessageCellContent>
                          </TableCell>
                          <TableCell $align="center" $isChild $isLastChild={isLastDomain} $hasNestedExpanded={isDomainExpanded && !!domain.latest_error}>
                            <SeverityBadge $severity={domain.severity_level}>
                              {domain.severity_level}
                            </SeverityBadge>
                          </TableCell>
                          <TableCell $align="center" $isChild $isLastChild={isLastDomain} $hasNestedExpanded={isDomainExpanded && !!domain.latest_error}>
                            {domain.count.toLocaleString()}
                          </TableCell>
                          <TableCell $align="center" $isChild $isLastChild={isLastDomain} $hasNestedExpanded={isDomainExpanded && !!domain.latest_error}>
                            {formatDate(domain.first_occurrence)}
                          </TableCell>
                          <TableCell $align="center" $isChild $isLastChild={isLastDomain} $hasNestedExpanded={isDomainExpanded && !!domain.latest_error}>
                            {formatDate(domain.latest_occurrence)}
                          </TableCell>
                        </TableRow>

                        {/* Latest Error Details */}
                        {isDomainExpanded && domain.latest_error && (
                          <TableRow $isNested $isLastNested={isLastDomain}>
                            <TableCell $isNested colSpan={6}>
                              <LatestErrorContainer>
                                <LatestErrorHeader>
                                  <LatestErrorTitle>{i18n.logAnalyzer.operations.topErrors.latestError}</LatestErrorTitle>
                                  <CopyButton
                                    onClick={() => copyToClipboard(formatErrorDetails(domain.latest_error), domainKey)}
                                    $copied={copiedErrorKeys.has(domainKey)}
                                    aria-label={i18n.logAnalyzer.operations.topErrors.copy}
                                  >
                                    <CopyIcon size={14} color={copiedErrorKeys.has(domainKey) ? '#FB6F2A' : 'currentColor'} />
                                    {copiedErrorKeys.has(domainKey) ? 'Copied' : i18n.logAnalyzer.operations.topErrors.copy}
                                  </CopyButton>
                                </LatestErrorHeader>
                                <ErrorDetails>{formatErrorDetails(domain.latest_error)}</ErrorDetails>
                              </LatestErrorContainer>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>

      {/* Filter Modal */}
      {filterable && (
        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleApplyFilters}
          config={filterConfig}
          initialValues={filterModalValues}
          title="Filters"
        />
      )}
    </TableWrapper>
  );
};

export default DrilldownTable;

